import {
  IGetAnyEventQuery,
  IGetAnyEventQueryVariables,
  IGetAnyTableEventQuery,
  IGetAnyTableEventQueryVariables,
  IGetBasicEventQuery,
  IGetBasicEventQueryVariables,
  IGetFieldQuery,
  IGetFieldQueryVariables,
  IGetNamedBucketDefQuery,
  IGetNamedBucketDefQueryVariables,
  IGetNamedFilterQuery,
  IGetNamedFilterQueryVariables,
  IGetNamedMeasureQuery,
  IGetNamedMeasureQueryVariables,
  IGetVirtualEventQuery,
  IGetVirtualEventQueryVariables,
  INamedCohortQueryQuery,
  INamedCohortQueryQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import GET_ANY_EVENT from '../../../pages/Formulas/apollo/GET_ANY_EVENT';
import { useCallback, useRef, useState } from 'react';
import { ILoadableEntity, ILoadableNode } from './types';
import {
  GET_ANY_TABLE_EVENT,
  GET_BASIC_EVENT,
  GET_FIELD_QUERY,
  GET_NAMED_BUCKET_DEF,
  GET_NAMED_COHORT,
  GET_VIRTUAL_EVENT,
} from '../../../pages/Formulas/apollo';
import { assertNotNil } from '../../../utils/types';
import { CREATABLE_ENTITY } from './constants';
import { useImmer } from 'use-immer';
import { getNodeId } from './utils';
import { isNil } from 'lodash';
import { UnreachableCaseError } from 'ts-essentials';
import { GET_NAMED_FILTER } from '../../../utils/apollo/filters/query';
import { GET_NAMED_MEASURE } from '../../../utils/apollo/measures';
import { useApolloClient } from '@apollo/client';
import { isNestedField } from '../../../utils/fields';

export const useEntityLoader = (schemaId: string) => {
  const client = useApolloClient();

  const [loadingCount, setLoadingCount] = useState(0);
  const pendingLoading = useRef(new Map<string, Promise<ILoadableEntity>>());
  const [entityCache, setEntityCache] = useImmer<
    Record<string, ILoadableEntity>
  >({});

  const getEntityFromCache = useCallback(
    (node: ILoadableNode): ILoadableEntity | undefined => {
      const id = getNodeId(node);
      return entityCache[id];
    },
    [entityCache],
  );

  const storeEntityInCache = useCallback(
    (node: ILoadableNode, entity: ILoadableEntity) => {
      const id = getNodeId(node);
      setEntityCache((draft) => {
        draft[id] = entity;
      });
    },
    [setEntityCache],
  );

  const loadEntityAsync = useCallback(
    async (node: ILoadableNode): Promise<ILoadableEntity> => {
      if (node.__typename === 'TagLeafAnyEvent') {
        return (
          await client.query<IGetAnyEventQuery, IGetAnyEventQueryVariables>({
            query: GET_ANY_EVENT,
          })
        ).data.anyEvent;
      } else if (node.__typename === 'TagLeafBasicEvent') {
        const entity = (
          await client.query<IGetBasicEventQuery, IGetBasicEventQueryVariables>(
            { query: GET_BASIC_EVENT, variables: { schemaId, key: node.key } },
          )
        ).data.basicEvent;
        assertNotNil(entity);
        return entity;
      } else if (node.__typename === 'TagLeafVirtualEvent') {
        return (
          await client.query<
            IGetVirtualEventQuery,
            IGetVirtualEventQueryVariables
          >({ query: GET_VIRTUAL_EVENT, variables: { id: node.id } })
        ).data.virtualEvent;
      } else if (node.__typename === 'TagLeafAnyTableEvent') {
        return (
          await client.query<
            IGetAnyTableEventQuery,
            IGetAnyTableEventQueryVariables
          >({
            query: GET_ANY_TABLE_EVENT,
          })
        ).data.anyTableEvent;
      } else if (node.__typename === '__CreatableNode') {
        return CREATABLE_ENTITY;
      } else if (
        node.__typename === 'TagLeafCompoundCohort' ||
        node.__typename === 'TagLeafSimpleCohort'
      ) {
        return (
          await client.query<
            INamedCohortQueryQuery,
            INamedCohortQueryQueryVariables
          >({ query: GET_NAMED_COHORT, variables: { id: node.id } })
        ).data.namedCohort;
      } else if (
        node.__typename === 'TagLeafSimpleMeasure' ||
        node.__typename === 'TagLeafCompoundMeasure'
      ) {
        return (
          await client.query<
            IGetNamedMeasureQuery,
            IGetNamedMeasureQueryVariables
          >({ query: GET_NAMED_MEASURE, variables: { id: node.id } })
        ).data.namedMeasure;
      } else if (node.__typename === 'TagLeafFilter') {
        return (
          await client.query<
            IGetNamedFilterQuery,
            IGetNamedFilterQueryVariables
          >({ query: GET_NAMED_FILTER, variables: { id: node.id } })
        ).data.namedFilter;
      } else if (
        node.__typename === 'TagLeafField' ||
        node.__typename === 'TagFieldInlineBranch' ||
        node.__typename === 'TagInlineBranch'
      ) {
        const result = (
          await client.query<IGetFieldQuery, IGetFieldQueryVariables>({
            query: GET_FIELD_QUERY,
            variables: { schemaId, key: node.key },
          })
        ).data.field;
        assertNotNil(result);
        if (isNestedField(result)) {
          return { __typename: '__NestedFieldEntity', field: result };
        } else {
          return result;
        }
      } else if (
        node.__typename === 'TagLeafRangeBucketDef' ||
        node.__typename === 'TagLeafDateBucketDef' ||
        node.__typename === 'TagLeafEnumBucketDef'
      ) {
        return (
          await client.query<
            IGetNamedBucketDefQuery,
            IGetNamedBucketDefQueryVariables
          >({ query: GET_NAMED_BUCKET_DEF, variables: { id: node.id } })
        ).data.namedBucket;
      } else if (node.__typename === 'TagLeafCustomBucketDef') {
        return {
          __typename: '__CustomBucketDef',
          kind: node.kind,
        };
      } else {
        throw new UnreachableCaseError(node);
      }
    },
    [client, schemaId],
  );

  // fetchEntity will always make an API call
  const fetchEntity = useCallback(
    async (node: ILoadableNode): Promise<ILoadableEntity> => {
      const nodeId = getNodeId(node);
      let pendingRequest = pendingLoading.current.get(nodeId);
      if (!pendingRequest) {
        pendingRequest = loadEntityAsync(node);
        pendingLoading.current.set(nodeId, pendingRequest);
        try {
          storeEntityInCache(node, await pendingRequest);
        } finally {
          pendingLoading.current.delete(nodeId);
        }
      }
      return await pendingRequest;
    },
    [loadEntityAsync, storeEntityInCache],
  );

  // first check the cache, if not exist, then make an API call
  const getEntityFromCacheOrFetch = useCallback(
    async (node: ILoadableNode): Promise<ILoadableEntity> => {
      setLoadingCount((prevState) => prevState + 1);
      try {
        let result = getEntityFromCache(node);
        if (!isNil(result)) {
          return result;
        } else {
          result = await fetchEntity(node);
          return result;
        }
      } finally {
        setLoadingCount((prevState) => Math.max(0, prevState - 1));
      }
    },
    [getEntityFromCache, fetchEntity],
  );

  return {
    fetchEntity,
    getEntityFromCacheOrFetch,
    loading: loadingCount > 0,
    getEntityFromCache,
  } as const;
};
